import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Loader, Send, Heart, TrendingUp, TrendingDown, Tag, ArrowUpCircle, ArrowDownCircle, Check, X } from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../../context/ThemeContext';
import { formatPrice } from '../../utils/formatters';
import TradingChart from '../charts/TradingChart';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function GlowCard({ color, style, children, onClick }) {
  return (
    <div onClick={onClick} className="app-card rounded-2xl" style={{
      background: `linear-gradient(135deg, ${color}14 0%, ${color}04 100%)`,
      border: `1px solid ${color}30`,
      ...style,
    }}>
      {children}
    </div>
  );
}

export default function TradeAnalysisPanel({ trade, relationshipId, onBack }) {
  const { colors } = useTheme();
  const ac = colors.accent;

  const [candles, setCandles] = useState(null);
  const [loadingChart, setLoadingChart] = useState(true);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const [text, setText] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [posting, setPosting] = useState(false);

  const loadCandles = useCallback(async () => {
    setLoadingChart(true);
    const symbol = trade.symbol;
    const asOfDate = (trade.exit_date || trade.executed_at || '').slice(0, 10);
    try {
      // Intraday trades get 15m resolution; try that first, fall back to daily
      // candles if no intraday data exists for the trade's date (e.g. backfilled trades).
      // GET /chart/{symbol} returns {symbol, interval, is_intraday, candles: [...]}, not a bare array.
      if (trade.product_type === 'INTRADAY') {
        try {
          const { data } = await axios.get(`${API}/chart/${symbol}`, { params: { interval: '15m', as_of_date: asOfDate || undefined } });
          if (Array.isArray(data?.candles) && data.candles.length > 0) { setCandles(data.candles); return; }
        } catch { /* fall through to daily */ }
      }
      const { data } = await axios.get(`${API}/chart/${symbol}`, { params: { interval: '1d', as_of_date: asOfDate || undefined } });
      setCandles(Array.isArray(data?.candles) ? data.candles : []);
    } catch { setCandles([]); }
    finally { setLoadingChart(false); }
  }, [trade]);

  const loadComments = useCallback(async () => {
    setLoadingComments(true);
    try {
      const { data } = await axios.get(`${API}/mentorship/relationships/${relationshipId}/comments`, {
        params: { trade_id: trade.trade_id },
      });
      setComments(data);
    } finally { setLoadingComments(false); }
  }, [relationshipId, trade]);

  useEffect(() => { loadCandles(); }, [loadCandles]);
  useEffect(() => { if (relationshipId) loadComments(); }, [relationshipId, loadComments]);

  const handlePost = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${API}/mentorship/relationships/${relationshipId}/comments`, {
        body: text.trim(), trade_id: trade.trade_id, parent_comment_id: replyTo,
      });
      setText(''); setReplyTo(null);
      loadComments();
    } catch { import('react-hot-toast').then(m => m.default.error('Failed to post comment')); }
    finally { setPosting(false); }
  };

  const handleLike = async (id) => {
    try {
      const { data } = await axios.post(`${API}/mentorship/comments/${id}/like`);
      setComments(prev => updateLike(prev, id, data));
    } catch { /* silent */ }
  };

  const updateLike = (list, id, data) => list.map(c => {
    if (c.id === id) return { ...c, like_count: data.like_count, liked_by_me: data.liked_by_me };
    if (c.replies?.length) return { ...c, replies: updateLike(c.replies, id, data) };
    return c;
  });

  const pnlColor = Number(trade.pnl) >= 0 ? '#16C784' : '#ef4444';
  const rr = trade.actual_r != null ? Number(trade.actual_r).toFixed(2) : null;
  const isLong = trade.trade_type === 'BUY';
  const disciplineSnap = (() => {
    let s = trade.discipline_snap;
    if (typeof s === 'string') { try { s = JSON.parse(s); } catch { s = null; } }
    return s || null;
  })();
  const holdTime = (() => {
    if (!trade.executed_at || !trade.exit_date) return null;
    const ms = new Date(trade.exit_date) - new Date(trade.executed_at);
    if (isNaN(ms) || ms < 0) return null;
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${m}m`;
  })();

  const Comment = ({ c, depth = 0 }) => (
    <div style={{ marginLeft: depth * 20 }}>
      <div className="px-3 py-2 rounded-lg" style={{ background: colors.inputBg, border: `1px solid ${colors.border}` }}>
        <div className="flex items-center justify-between">
          <p className="text-[12px] font-semibold" style={{ color: colors.primaryText }}>{c.author_name}</p>
          <p className="text-[11px]" style={{ color: colors.secondaryText }}>{new Date(c.created_at).toLocaleString()}</p>
        </div>
        <p className="text-[13px] mt-1" style={{ color: colors.primaryText }}>{c.body}</p>
        <div className="flex items-center gap-3 mt-1.5">
          <button onClick={() => handleLike(c.id)} className="flex items-center gap-1 text-[11px]" style={{ color: c.liked_by_me ? '#ef4444' : colors.secondaryText }}>
            <Heart className="w-3 h-3" fill={c.liked_by_me ? '#ef4444' : 'none'} /> {c.like_count}
          </button>
          <button onClick={() => setReplyTo(c.id)} className="text-[11px]" style={{ color: ac }}>Reply</button>
        </div>
      </div>
      {(c.replies || []).map(r => <div key={r.id} className="mt-1.5"><Comment c={r} depth={depth + 1} /></div>)}
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-4 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="app-card p-1.5 rounded-md flex-shrink-0" style={{ background: `linear-gradient(135deg, ${ac}12 0%, ${ac}04 100%)` }}>
            <ArrowLeft className="w-4 h-4" style={{ color: colors.primaryText }} />
          </button>
          <div className="min-w-0">
            <h1 className="text-[16px] font-bold truncate" style={{ color: colors.primaryText }}>{(trade.symbol || '').replace('.NS', '')}</h1>
            <p className="text-[12px] truncate" style={{ color: colors.secondaryText }}>
              {trade.product_type}{trade.company_name ? ` · ${trade.company_name}` : ''}
            </p>
          </div>
        </div>
        <span className="text-[12px] px-2.5 py-1 rounded-full font-bold flex items-center gap-1.5 flex-shrink-0 w-fit sm:ml-auto"
          style={{
            background: isLong ? 'rgba(22,199,132,0.14)' : 'rgba(239,68,68,0.14)',
            color: isLong ? '#16C784' : '#ef4444',
            border: `1px solid ${isLong ? 'rgba(22,199,132,0.3)' : 'rgba(239,68,68,0.3)'}`,
          }}>
          {isLong ? <ArrowUpCircle className="w-3.5 h-3.5" /> : <ArrowDownCircle className="w-3.5 h-3.5" />}
          {isLong ? 'LONG (BUY)' : 'SHORT (SELL)'}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <GlowCard color={pnlColor} style={{ padding: 12 }}>
          <p className="text-[11px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>P&L</p>
          <p className="text-[16px] font-bold mt-0.5" style={{ color: pnlColor }}>
            {trade.pnl != null ? formatPrice(trade.pnl) : '—'}
            {trade.pnl_pct != null && <span className="text-[12px] font-semibold ml-1">({trade.pnl_pct >= 0 ? '+' : ''}{Number(trade.pnl_pct).toFixed(2)}%)</span>}
          </p>
        </GlowCard>
        <GlowCard color={ac} style={{ padding: 12 }}>
          <p className="text-[11px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>R:R Achieved</p>
          <p className="text-[16px] font-bold mt-0.5" style={{ color: ac }}>{rr ? `${rr}R` : '—'}</p>
        </GlowCard>
        <GlowCard color={ac} style={{ padding: 12 }}>
          <p className="text-[11px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>Holding Time</p>
          <p className="text-[16px] font-bold mt-0.5" style={{ color: colors.primaryText }}>{holdTime || '—'}</p>
        </GlowCard>
        <GlowCard color={ac} style={{ padding: 12 }}>
          <p className="text-[11px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>Status</p>
          <p className="text-[16px] font-bold mt-0.5" style={{ color: colors.primaryText }}>{trade.status}{trade.exit_reason ? ` · ${trade.exit_reason.replace(/_/g, ' ')}` : ''}</p>
        </GlowCard>
      </div>

      {/* Trade details: direction, size, and discipline checklist */}
      <GlowCard color={ac} style={{ padding: 14 }}>
        <p className="text-[13px] font-semibold mb-3" style={{ color: colors.primaryText }}>Trade Details</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <div>
            <p className="text-[11px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>Direction</p>
            <p className="text-[13px] font-semibold" style={{ color: isLong ? '#16C784' : '#ef4444' }}>{isLong ? 'Long (Buy)' : 'Short (Sell)'}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>Product Type</p>
            <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>{trade.product_type || '—'}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>Quantity</p>
            <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>{trade.quantity ?? '—'}{trade.lot_qty > 1 ? ` (${trade.lot_qty} lots)` : ''}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>Exit Reason</p>
            <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>{trade.exit_reason ? trade.exit_reason.replace(/_/g, ' ') : '—'}</p>
          </div>
        </div>
        {disciplineSnap && (
          <div>
            <p className="text-[11px] uppercase tracking-wide mb-1.5" style={{ color: colors.secondaryText }}>Discipline Checklist</p>
            <div className="flex flex-wrap gap-3">
              {[
                { label: 'Stop Loss Set', val: disciplineSnap.sl_placed },
                { label: 'Tagged', val: disciplineSnap.tagged },
                { label: 'Journaled', val: disciplineSnap.journaled },
              ].filter(d => d.val != null).map(({ label, val }) => (
                <span key={label} className="text-[12px] flex items-center gap-1" style={{ color: val ? '#16C784' : '#ef4444' }}>
                  {val ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />} {label}
                </span>
              ))}
              {disciplineSnap.exit_quality && (
                <span className="text-[12px]" style={{ color: colors.secondaryText }}>
                  Exit quality: <b style={{ color: colors.primaryText }}>{disciplineSnap.exit_quality}</b>
                </span>
              )}
            </div>
          </div>
        )}
      </GlowCard>

      <GlowCard color={ac} style={{ padding: 8 }}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 px-2 pt-2 pb-3">
          {[
            { label: 'Entry', value: trade.price },
            { label: 'Exit', value: trade.exit_price },
            { label: 'Stop Loss', value: trade.stop_loss },
            { label: 'Target', value: trade.target_price },
          ].map(({ label, value }) => (
            <div key={label}>
              <p className="text-[11px]" style={{ color: colors.secondaryText }}>{label}</p>
              <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>{value != null ? formatPrice(value) : '—'}</p>
            </div>
          ))}
        </div>
        {loadingChart ? (
          <div className="flex justify-center py-20"><Loader className="w-5 h-5 animate-spin" style={{ color: colors.secondaryText }} /></div>
        ) : candles && candles.length > 0 ? (
          <TradingChart
            symbol={trade.symbol}
            height={420}
            actMode={true}
            actStaticMode={true}
            preloadedCandles={candles}
            startFromIndex={candles.length - 1}
            actEntryPrice={trade.price != null ? Number(trade.price) : null}
            actExitPrice={trade.exit_price != null ? Number(trade.exit_price) : null}
            actStopLoss={trade.stop_loss != null ? Number(trade.stop_loss) : null}
            actTargetPrice={trade.target_price != null ? Number(trade.target_price) : null}
            actSide={trade.trade_type}
            hideOutcome={true}
          />
        ) : (
          <div className="flex justify-center py-20">
            <p className="text-[12px]" style={{ color: colors.secondaryText }}>No chart data available for this trade</p>
          </div>
        )}
      </GlowCard>

      {/* Confidence, tags, reasoning, discipline impact */}
      {(trade.confidence_score != null || (trade.strategy_tags || []).length > 0 || trade.reasoning || trade.discipline_delta) && (
        <GlowCard color={ac} style={{ padding: 14 }}>
          <p className="text-[13px] font-semibold mb-3" style={{ color: colors.primaryText }}>Trade Context</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              {trade.confidence_score != null && (
                <div>
                  <p className="text-[11px] uppercase tracking-wide mb-1" style={{ color: colors.secondaryText }}>Confidence Score</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full" style={{ background: colors.inputBg }}>
                      <div className="h-1.5 rounded-full" style={{
                        width: `${trade.confidence_score * 10}%`,
                        background: trade.confidence_score >= 7 ? '#16C784' : trade.confidence_score >= 4 ? '#f59e0b' : '#ef4444',
                      }} />
                    </div>
                    <span className="text-[13px] font-bold" style={{ color: colors.primaryText }}>{trade.confidence_score}/10</span>
                  </div>
                </div>
              )}
              {(trade.strategy_tags || []).length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wide mb-1.5 flex items-center gap-1" style={{ color: colors.secondaryText }}>
                    <Tag className="w-3 h-3" /> Strategy Tags
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {trade.strategy_tags.map(tag => (
                      <span key={tag} className="text-[11px] px-2 py-0.5 rounded-full font-medium"
                        style={{ background: `${ac}18`, color: ac, border: `1px solid ${ac}30` }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {trade.reasoning && (
                <div>
                  <p className="text-[11px] uppercase tracking-wide mb-1" style={{ color: colors.secondaryText }}>Entry Reasoning</p>
                  <p className="text-[13px]" style={{ color: colors.primaryText }}>{trade.reasoning}</p>
                </div>
              )}
            </div>
            <div>
              {trade.discipline_delta && (
                <div>
                  <p className="text-[11px] uppercase tracking-wide mb-1.5" style={{ color: colors.secondaryText }}>Discipline Score Impact</p>
                  <div className="flex items-center gap-2 mb-1.5">
                    {trade.discipline_delta.delta > 0 ? (
                      <TrendingUp className="w-4 h-4" style={{ color: '#16C784' }} />
                    ) : trade.discipline_delta.delta < 0 ? (
                      <TrendingDown className="w-4 h-4" style={{ color: '#ef4444' }} />
                    ) : null}
                    <span className="text-[15px] font-bold" style={{ color: trade.discipline_delta.delta > 0 ? '#16C784' : trade.discipline_delta.delta < 0 ? '#ef4444' : colors.secondaryText }}>
                      {trade.discipline_delta.delta > 0 ? '+' : ''}{trade.discipline_delta.delta} points
                    </span>
                  </div>
                  {(trade.discipline_delta.reasons || []).length > 0 && (
                    <ul className="space-y-1">
                      {trade.discipline_delta.reasons.map((r, i) => (
                        <li key={i} className="text-[12px]" style={{ color: colors.secondaryText }}>• {r}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {trade.ai_review && (
                <div className="mt-3">
                  <p className="text-[11px] uppercase tracking-wide mb-1" style={{ color: colors.secondaryText }}>AI Review</p>
                  <p className="text-[12px] italic" style={{ color: colors.secondaryText }}>{trade.ai_review}</p>
                </div>
              )}
            </div>
          </div>
        </GlowCard>
      )}

      {relationshipId && (
        <GlowCard color={ac} style={{ padding: 14 }}>
          <p className="text-[13px] font-semibold mb-2.5" style={{ color: colors.primaryText }}>
            {replyTo ? 'Mentor Feedback — replying' : 'Mentor Feedback'}
          </p>
          <div className="flex gap-2 mb-3">
            <input
              type="text" placeholder="Comment on this trade..."
              value={text} onChange={e => setText(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg text-[13px]"
              style={{ background: colors.inputBg, color: colors.primaryText, border: `1px solid ${colors.border}` }}
            />
            {replyTo && (
              <button onClick={() => setReplyTo(null)} className="px-2 rounded-lg text-[11px]" style={{ color: colors.secondaryText, border: `1px solid ${colors.border}` }}>
                Cancel
              </button>
            )}
            <button onClick={handlePost} disabled={posting || !text.trim()}
              className="px-3 rounded-lg" style={{ background: `linear-gradient(135deg, ${ac}, #8b5cf6)`, boxShadow: `0 2px 10px ${ac}40`, opacity: !text.trim() ? 0.5 : 1 }}>
              {posting ? <Loader className="w-4 h-4 animate-spin text-white" /> : <Send className="w-4 h-4 text-white" />}
            </button>
          </div>
          {loadingComments ? (
            <div className="flex justify-center py-4"><Loader className="w-4 h-4 animate-spin" style={{ color: colors.secondaryText }} /></div>
          ) : comments.length === 0 ? (
            <p className="text-[12px] text-center py-3" style={{ color: colors.secondaryText }}>No feedback yet on this trade</p>
          ) : (
            <div className="space-y-2">
              {comments.map(c => <Comment key={c.id} c={c} />)}
            </div>
          )}
        </GlowCard>
      )}
    </div>
  );
}
